import React, { useCallback, useMemo, useState } from "react";
import { useContactContext } from "../../../context/ContactContext/context";
import { IUser } from "../../../types";
import { useTypedDispatch, useTypedSelector } from "../../../redux/hooks";
import {
    selectCallingShortcodesOnUser,
    selectChatEnabled,
    selectCurrentUser,
    selectDepartmentByUserUuid,
    selectIsNebulaChat,
    selectUserSpecificQueueLogin,
    selectUserStatus,
    setActiveChat,
    setViewContact,
    startCall,
} from '../../../redux/slices';
import { usePolyglot } from "../../../context/Polyglot";
import { useCallContact } from "../../../components/sidebars/Contact/hooks";
import StyledDropdown, { StyledDropdownOption } from "../../../components/StyledComponents/StyledDropdown";
import styles from "../syles.module.scss";
import { StyledButton } from "../../../components/StyledComponents";
import Icon from "../../../components/Icon";
import { isPreview } from "../../../helpers";
import DepartmentTag from "../../../components/buttons/DepartmentTag";
import AuxCodeController from "../../../pages/home/children/AuxCodeController/AuxCodeController";
import CallIconButton from "../../../components/buttons/CallIconButton";
import { selectSortedQueuePanels } from "../../../redux/slices/queueSlice";
import {
    useGetQueuePanelsForAgentsQuery,
    useLazyGetQueuePanelDataQuery,
    usePutQueueLoginForAgentsMutation
} from "../../../redux/services/queueApi";
import LoadingSpinner from "../../../components/loading/LoadingSpinner";
import { ReactComponent as LoginIcon } from '../../../img/misc/login.svg';
import { ReactComponent as LogoutIcon } from '../../../img/misc/logout.svg';
import { Slider } from "../../../components";
import { useAppContext } from "../../../context/AppContext/AppContext";
import { useHasQueuePanel } from "../../../pages/phone";
import { setActiveConversation } from "../../../redux/slices/chatNSlice";

interface OptionProps {
    inTag?: boolean;
}

interface QueueLoginStatus {
    allQueues: boolean;
    specificQueue: boolean
}

export const UserOptions: React.FC<OptionProps> = ({ inTag }) => {
    const isNebulaChat = useTypedSelector(selectIsNebulaChat);

    const contact = useContactContext().contact as IUser;

    const chatEnabled = useTypedSelector(selectChatEnabled);
    const userStatus = useTypedSelector(state => selectUserStatus(state, contact.name));
    const {
        pickup: pickupShortcode,
        listen: listenShortcode,
        whisper: whisperShortcode,
        barge: bargeShortcode
    } = useTypedSelector(state => selectCallingShortcodesOnUser(state, contact.uuid));

    const { t } = usePolyglot();
    const { callContact } = useCallContact()

    const dispatch = useTypedDispatch();

    const openChat = () => {
        if (isNebulaChat) {
            dispatch(setActiveConversation(contact.uuid))
        } else {
            dispatch(setActiveChat(contact.uuid));
        }
        dispatch(setViewContact())
    };

    type ExtraOptionsValue = 'barge' | 'listen' | 'whisper';

    const extraOptions: StyledDropdownOption[] = [];

    if (listenShortcode) {
        extraOptions.push({
            label: t("actions.listen"),
            value: 'listen'
        })
    }

    if (whisperShortcode) {
        extraOptions.push({
            label: t("actions.whisper"),
            value: 'whisper',
        })
    }

    if (bargeShortcode) {
        extraOptions.push({
            label: t("actions.join"),
            value: 'barge'
        })
    }

    const handleExtraOption = (option: ExtraOptionsValue) => {
        switch (option) {
            case "barge":
                if (!bargeShortcode) return;
                dispatch(
                    startCall(
                        `${bargeShortcode}${contact.extension}`,
                        t("phrases.joining_to_%n", { n: contact.nickname })
                    )
                )
                break;
            case "listen":
                if (!listenShortcode) return;
                dispatch(
                    startCall(
                        `${listenShortcode}${contact.extension}`,
                        t("phrases.listening_to_%n", { n: contact.nickname })
                    )
                )
                break;
            case "whisper":
                if (!whisperShortcode) return;
                dispatch(
                    startCall(
                        `${whisperShortcode}${contact.extension}`,
                        t("phrases.whispering_to_%n", { n: contact.nickname })
                    )
                )
                break;
        }
        dispatch(setViewContact())
    }

    return (
        <>
            <div className={styles.mainOptions}>
                <CallIconButton
                    user={contact}
                    handleCall={() => callContact()}
                />
                {contact?.personal_mailbox ? (
                    <StyledButton
                        buttonStyle='text'
                        onClick={() =>
                            dispatch(
                                startCall(
                                    `**${contact.extension}`,
                                    `${contact.nickname}'s Mailbox`
                                )
                            )
                        }
                        title="Leave Message"
                        iconOnly
                    >
                        <Icon name='voicemail' ariaHidden width={22} height={22} />
                    </StyledButton>
                ) : null}
                {chatEnabled ? (
                    <StyledButton
                        buttonStyle='text'
                        onClick={() => openChat()}
                        title={t("actions.chat")}
                        iconOnly
                    >
                        <Icon name='messageFilled' ariaHidden width={22} height={22} />
                    </StyledButton>
                ) : null}
                {isPreview ? (
                    <StyledButton
                        buttonStyle='text'
                        title={t("terms.video", 1)}
                        iconOnly
                    >
                        <Icon name='videoFilled' ariaHidden width={22} height={22} />
                    </StyledButton>
                ) : null}
                {!inTag && userStatus && userStatus.status === 'early' && pickupShortcode && (
                    <StyledButton
                        buttonStyle='text'
                        onClick={() =>
                            dispatch(
                                startCall(
                                    `${pickupShortcode}${contact.extension}`,
                                    `${t("actions.pick_up")} ${contact.nickname}`
                                )
                            )
                        }
                        title={t("actions.pick_up")}
                        iconOnly
                    >
                        <Icon name='callOutgoing' ariaHidden width={22} height={22} />
                    </StyledButton>
                )}
            </div>
            {!inTag && extraOptions.length > 0 ? (
                <StyledDropdown
                    onSelect={(val: string) => handleExtraOption(val as ExtraOptionsValue)}
                    options={extraOptions}
                    display={(<p>More</p>)}
                    buttonStyle='text'
                    noDownArrow
                    className={styles.extra_opts}
                />
            ) : null}
        </>
    )
}

export const UserMain: React.FC = () => {
    const contact = useContactContext().contact as IUser;

    const { darkMode } = useAppContext();
    const { t } = usePolyglot();
    const hasQueuePanel = useHasQueuePanel();

    const [loadingQueueId, setLoadingQueueId] = useState<string | null>(null);

    const appUser = useTypedSelector(selectCurrentUser);
    const departments = useTypedSelector(state => selectDepartmentByUserUuid(state, contact.uuid)) || []
    const userQueues = useTypedSelector(selectSortedQueuePanels)
    const status = useTypedSelector(state => selectUserStatus(state, contact.name));

    const { data: panels, isLoading: userPanelsLoading } = useGetQueuePanelsForAgentsQuery(contact.uuid, {
        refetchOnMountOrArgChange: true,
        skip: !hasQueuePanel
    });

    const [triggerGetQueuePanelData] = useLazyGetQueuePanelDataQuery();

    const userUuid = contact.uuid;
    const userName = contact.name;
    const isOffline = status?.status === 'closed';
    const isQueueLogin = status?.queue_login ?? false;

    const loginStatusesByQueue = useTypedSelector(state => {
        const map: Record<string, QueueLoginStatus> = {};
        if (!panels || !Array.isArray(panels)) return map;
        panels.forEach(p => {
            map[p.queue_uuid] = selectUserSpecificQueueLogin(state, p.queue_uuid, userName, userUuid) as QueueLoginStatus;
        });
        return map;
    });

    const supervisedQueueNames = useMemo(() => {
        if (!Array.isArray(userQueues)) return [];
        return userQueues
            .filter(
                (uq) =>
                    uq.is_supervisor &&
                    Array.isArray(uq.queue_users) &&
                    uq.queue_users.includes(contact.uuid)
            )
            .map((uq) => uq.name);
    }, [userQueues, contact.uuid]);

    const myPanels = useMemo(() => {
        if (!panels || supervisedQueueNames.length === 0) return [];
        return panels.filter((p) => supervisedQueueNames.includes(p.name));
    }, [panels, supervisedQueueNames]);

    const isSupervisor = supervisedQueueNames.length > 0;

    const [putQueueLogin, { isLoading: isQueueLoginAgentsLoading }] = usePutQueueLoginForAgentsMutation();

    const sortedPanels = useMemo(() => {
        if (!panels) return [];
        return [...panels].sort((a, b) => {
            const isAInMyPanels = myPanels?.some((panel) => panel.queue_uuid === a.queue_uuid);
            const isBInMyPanels = myPanels?.some(panel => panel.queue_uuid === b.queue_uuid);
            return isBInMyPanels - isAInMyPanels;
        });
    }, [panels, myPanels]);

    const myPanelUuids = useMemo(() => {
        const s = new Set<string>();
        myPanels.forEach((panel) => {
            s.add(panel.queue_uuid);
        });
        return s;
    }, [myPanels]);

    const handleAllQueuesToggle = useCallback(() => {
        putQueueLogin({
            voip_user_uuid: contact.uuid,
            data: { login: !(status?.queue_login ?? false) },
        });

    }, [putQueueLogin, contact.uuid, status?.queue_login]);


    const handleSingleQueueToggle = useCallback(
        async (queueStatus: QueueLoginStatus, q: typeof sortedPanels[number]) => {
            const login = queueStatus?.allQueues ? !queueStatus.specificQueue : false;
            setLoadingQueueId(q.queue_uuid);

            try {
                await putQueueLogin({
                    voip_user_uuid: contact.uuid,
                    data: {
                        login,
                        queue_id: q.queue_uuid,
                    },
                }).unwrap();

                await triggerGetQueuePanelData({ voip_user_uuid: contact.uuid });
            } catch (err) {
                console.error(err);
            } finally {
                setLoadingQueueId(null);
            }
        },
        [putQueueLogin, contact.uuid, triggerGetQueuePanelData]
    );

    return (
        <>
            {hasQueuePanel && userPanelsLoading ? (<LoadingSpinner color="mamba" size={16} />) : null}
            {hasQueuePanel && !userPanelsLoading && isSupervisor && (
                <>
                    <div className={`${styles.queue_login} ${isOffline ? styles.offline : styles.online}`}>
                        <label htmlFor='CallQueueAvailability'>
                            Call Queues Login
                        </label>
                        <div className={styles.loading}>
                            <Slider
                                name="CallQueueAvailability"
                                disabled={isQueueLoginAgentsLoading || isOffline}
                                checked={status?.queue_login ?? false}
                                onChange={handleAllQueuesToggle}
                            />
                        </div>
                    </div>
                    <div
                        className={`${styles.item} ${isOffline ? styles.offline : styles.online} ${!isQueueLogin ? styles.offline : ''}`}>
                        <label htmlFor="agentQueues">Agent Queues</label>
                        <div className={styles.departments}>
                            {sortedPanels?.map(q => {
                                const isMyPanel = myPanelUuids.has(q.queue_uuid);
                                const isLoggedInForThisQueue = loginStatusesByQueue[q.queue_uuid];
                                const isThisQueueLoading = loadingQueueId === q.queue_uuid;

                                return (
                                    <div data-is-dark={darkMode} className={styles.queues} key={q.id}>
                                        <p className={isLoggedInForThisQueue.specificQueue && isQueueLogin ? "" : styles.inactive}>
                                            {q.name}
                                        </p>
                                        <div>
                                            {isMyPanel && (
                                                <>
                                                    {isThisQueueLoading ? (
                                                        <div className={styles.loading}>
                                                            <LoadingSpinner color="mamba" />
                                                        </div>
                                                    ) : (
                                                        <div
                                                            className={`${styles.loginIcon} ${isLoggedInForThisQueue.specificQueue ? styles.logout : styles.login}`}
                                                            onClick={() => handleSingleQueueToggle(isLoggedInForThisQueue, q)}
                                                        >
                                                            {isLoggedInForThisQueue.specificQueue ? <LogoutIcon /> :
                                                                <LoginIcon />}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}
            {(appUser.allow_custom_aux_codes || isPreview) || (hasQueuePanel && !userPanelsLoading && isSupervisor) ? (
                <div
                    className={[styles.item, styles.dropdown, isOffline ? styles.offline : ''].join(' ')}
                >
                    <label htmlFor='aux_code'>
                        Call Queue Status
                    </label>
                    <AuxCodeController
                        user_data={contact.uuid === appUser.uuid ? undefined : {
                            user_name: contact.name,
                            user_uuid: contact.uuid,
                        }}
                    />
                </div>
            ) : null}
            <div className={styles.item}>
                <label>
                    {t("terms.extension", 1)}
                </label>
                <div>
                    <p>
                        {contact.extension}
                    </p>
                </div>
            </div>
            <div className={styles.item}>
                <label>
                    {t("terms.department", 2)}
                </label>
                <div className={styles.departments}>
                    {departments.map(dept => (
                        <DepartmentTag
                            key={dept.uuid}
                            department={dept}
                        />
                    ))}
                    {departments.length < 1 ? (
                        <p>
                            User is not in any departments
                        </p>
                    ) : null}
                </div>
            </div>
        </>
    )
}
